import * as Sentry from '@sentry/capacitor';
import {
  removeHTMLTags,
  maybeTransformEmptyRichTextContentToNull,
} from 'shared/utils';
import { getTimeAgo } from 'shared/dates';
import { TFunction } from 'i18next';
import { isSupportedEmbedUrl } from 'shared/embedHelpers';
import { EvacZone } from 'shared/types';
import { NotificationType } from 'features/notifications/notifications.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Flooding } from 'features/flooding/flooding.types';
import { Hazard } from 'features/hazards/hazard.types';
import { Yup, CoordType } from 'shared/yup-extended';
import { getReportMedia } from 'components/MediaForm';
import { AlertCamera } from 'features/cameras/cameras.types';
import { QueryClient } from '@tanstack/react-query';
import { AnyObject } from 'yup/lib/types';
import { getAcreageAndContainmentValidationSchema } from 'features/wildfires/utils/getAcreageAndContainmentValidationSchema';
import { getReportMessageValidationSchema } from 'features/wildfires/utils/getReportMessageValidationSchema';
import { getEvacZoneStatusesValidationSchema } from 'features/geoEvents/utils/getEvacZoneStatusesValidationSchema';
import { getEvacuationFieldsValidationSchema } from 'features/evacZones/utils/getEvacuationFieldsValidationSchema';
import {
  getInitialEvacZonesVisibleState,
  isFloodingGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import { validateDeactivationForEvacZones } from 'features/geoEvents/validateDeactivationForEvacZones';
import { API } from 'api';
import { constructPatch } from 'features/geoEvents/hooks/useGeoEvent';
import { CreateReportProvisionalSubmissionResponse } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { ReporterNotesChanges } from 'features/reporterNotes/reporterNotes.types';
import {
  buildReporterNotesPayload,
  reporterNotesChangesToFormValues,
  reporterNotesDefaultFormValues,
  reporterNotesValidationSchema,
} from 'features/reporterNotes/reporterNotes.utils';
import {
  WildfireReportFormValues,
  PostReportData,
  ReportFormQueryParams,
  ReportableGeoEvent,
  ReportableGeoEventCreateUpdateData,
  ReportFormValues,
  GeoEventFormStatus,
  GeoEventPatchConfigs,
  FloodingReportFormValues,
} from './types';

export type {
  ReportableGeoEvent,
  ReportableGeoEventCreateUpdateData,
  ReportFormValues,
};

export type GeoEventReportFeatures = {
  supportsNotifications: boolean;
  supportsEvacZones: boolean;
  supportInternalNotes: boolean;
};

type ShouldShowAcreageWarningArgs = {
  initialAcreage: number | null;
  acreage: number | null;
  notificationType: NotificationType;
  containment?: number | null;
  isFps?: boolean;
  manualDeactivationStarted?: boolean | null;
};

export const isWildfireFormValues = (
  values: ReportFormValues,
): values is WildfireReportFormValues => 'isFps' in values;

export const getGeoEventReportFeatures = (
  geoEvent: ReportableGeoEvent,
): GeoEventReportFeatures => {
  const isHazard = geoEvent.geoEventType === 'hazard';
  return {
    supportsNotifications: !isHazard,
    supportsEvacZones: !isHazard,
    supportInternalNotes: !isHazard,
  };
};

export const getReportValuesForServer = (
  values: ReportFormValues & { mediaUrl?: string },
): PostReportData => {
  const {
    criticalAlertRegionIds,
    embedUrl,
    geoEventId,
    messageHtml,
    notificationType,
  } = values;

  return {
    message: messageHtml,
    embedUrl,
    geoEventId,
    notificationType,
    media: getReportMedia(values),
    ...(notificationType === NotificationType.Critical &&
      criticalAlertRegionIds.length > 0 && { criticalAlertRegionIds }),
  };
};

const getTypeSpecificGeoEventData = (input: {
  currentGeoEvent: ReportableGeoEvent;
  updates: ReportFormValues;
}): Partial<ReportableGeoEventCreateUpdateData> => {
  const { currentGeoEvent, updates } = input;

  const evacData = {
    evacuationNotes: maybeTransformEmptyRichTextContentToNull(
      updates.evacuationNotes,
    ),
    evacuationOrders: maybeTransformEmptyRichTextContentToNull(
      updates.evacuationOrders,
    ),
    evacuationWarnings: maybeTransformEmptyRichTextContentToNull(
      updates.evacuationWarnings,
    ),
    evacuationAdvisories: maybeTransformEmptyRichTextContentToNull(
      updates.evacuationAdvisories,
    ),
    evacuationShelterInPlace: maybeTransformEmptyRichTextContentToNull(
      updates.evacuationShelterInPlace,
    ),
    hasCustomEvacuationAdvisories: updates.customAdvisories,
    hasCustomEvacuationOrders: updates.customOrders,
    hasCustomEvacuationWarnings: updates.customWarnings,
    hasCustomEvacuationShelterInPlace: updates.customShelterInPlace,
    reporterOnlyNotes: maybeTransformEmptyRichTextContentToNull(
      updates.reporterOnlyNotes,
    ),
    temporarilyDisplayEvacZones: updates.temporarilyDisplayEvacZones,
  };

  const dataByType: Record<
    ReportableGeoEvent['geoEventType'],
    () => Partial<ReportableGeoEventCreateUpdateData>
  > = {
    wildfire: () => {
      const wildfireGeoEvent = currentGeoEvent as Wildfire;
      const wildfireUpdates = updates as WildfireReportFormValues;
      return {
        data: {
          ...wildfireGeoEvent.data,
          ...evacData,
          acreage: wildfireUpdates.acreage,
          containment: wildfireUpdates.containment,
          isFps: wildfireUpdates.isFps,
        },
      };
    },
    flooding: () => {
      const floodingGeoEvent = currentGeoEvent as Flooding;
      const floodingUpdates = updates as FloodingReportFormValues;
      return {
        temporarilyDisplayEvacZones:
          floodingUpdates.temporarilyDisplayEvacZones,
        hasCustomEvacuationOrders: floodingUpdates.customOrders,
        hasCustomEvacuationWarnings: floodingUpdates.customWarnings,
        hasCustomEvacuationAdvisories: floodingUpdates.customAdvisories,
        hasCustomEvacuationShelterInPlace: floodingUpdates.customShelterInPlace,
        data: {
          ...floodingGeoEvent.data,
          ...evacData,
        },
      };
    },
    hazard: () => {
      const hazardGeoEvent = currentGeoEvent as Hazard;
      return {
        data: {
          ...hazardGeoEvent.data,
        },
      };
    },
  };

  return dataByType[currentGeoEvent.geoEventType]();
};

export const getGeoEventValuesForServer = (input: {
  currentGeoEvent: ReportableGeoEvent;
  updates: ReportFormValues;
}): ReportableGeoEventCreateUpdateData => {
  const { currentGeoEvent, updates } = input;

  const releasePendingZoneUids = updates.releasePendingZoneUids?.length
    ? updates.releasePendingZoneUids
    : undefined;

  const mappedEvacZoneStatuses = updates.evacZoneStatuses.map((eZS) => ({
    status: eZS.status,
    evacZone: { id: eZS.evacZone.id },
  }));

  const specificGeoEventData = getTypeSpecificGeoEventData({
    currentGeoEvent,
    updates,
  });

  return {
    ...currentGeoEvent,
    isActive: updates.status === 'active',
    evacZoneStatuses: mappedEvacZoneStatuses,
    releasePendingZoneUids,
    reporterNotes: buildReporterNotesPayload(updates),
    ...specificGeoEventData,
    data: {
      ...specificGeoEventData.data,
      manualDeactivationStarted: updates.manualDeactivationStarted,
    },
  } as ReportableGeoEventCreateUpdateData;
};

export const getValuesForServer = (input: {
  currentGeoEvent: ReportableGeoEvent;
  updates: ReportFormValues;
  mediaUrl?: string;
  geoEventPatchConfigs: GeoEventPatchConfigs;
}): PostReportData => {
  const reportData = getReportValuesForServer({
    ...input.updates,
    mediaUrl: input.mediaUrl,
  });
  const geoEventData = getGeoEventValuesForServer({
    currentGeoEvent: input.currentGeoEvent,
    updates: input.updates,
  });

  const { dataPatchConfigs, fieldPatchConfigs = {} } =
    input.geoEventPatchConfigs;

  const geoEventPatchData = constructPatch(
    input.currentGeoEvent,
    geoEventData,
    dataPatchConfigs,
    fieldPatchConfigs,
  );

  if (geoEventPatchData) {
    return {
      ...reportData,
      geoEventUpdate: geoEventPatchData,
    };
  }

  return reportData;
};

export const SILENT_ACREAGE_WARNING_THRESHOLD = 0.2;

export const isWildfireUnderControl = ({
  containment,
  isFps,
  manualDeactivationStarted,
}: {
  containment?: number | null;
  isFps?: boolean | null;
  manualDeactivationStarted?: boolean | null;
}): boolean => {
  return (
    (containment != null && containment >= 50) ||
    Boolean(isFps) ||
    Boolean(manualDeactivationStarted)
  );
};

export const shouldShowAcreageWarning = ({
  initialAcreage,
  acreage,
  notificationType,
  containment,
  isFps,
  manualDeactivationStarted,
}: ShouldShowAcreageWarningArgs): boolean => {
  if (
    isWildfireUnderControl({ containment, isFps, manualDeactivationStarted })
  ) {
    return false;
  }

  return (
    notificationType === NotificationType.Silent &&
    initialAcreage != null &&
    initialAcreage !== 0 &&
    acreage != null &&
    (acreage - initialAcreage) / initialAcreage >
      SILENT_ACREAGE_WARNING_THRESHOLD
  );
};

export const hasEvacInfoChanged = (
  prev: string | null,
  next: string,
): boolean => {
  const prevValue = (prev || '').trim();
  const nextValue = next.trim();
  return prevValue !== nextValue;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRedundancyMessage = (errorResponse: any): string[] => {
  const { data: errorData } = errorResponse;

  if (errorData.length === 1) {
    const recentReport = errorData[0];
    const creator = recentReport.userCreated?.email || 'Someone';
    const timeSince = getTimeAgo(recentReport.dateCreated);
    const plainTextMessage = removeHTMLTags(recentReport.message);

    return [
      `${creator} just posted ${timeSince}:`,
      `"${plainTextMessage}"`,
      'Are you sure you still want to post this report?',
    ];
  }

  return [
    'Too many reports in the last few minutes for this incident!',
    'Are you sure you still want to post this report?',
  ];
};

export const statusToTransKey: Record<GeoEventFormStatus, string> = {
  inactive: 'common.inactive',
  active: 'common.active',
};

export const getCoordinateValidationSchema = (input: {
  t: TFunction;
  coordType: CoordType;
  whenPropKey: string;
}): Yup.StringSchema<string | undefined, AnyObject, string | undefined> => {
  const { t, coordType, whenPropKey } = input;
  return Yup.string()
    .coordinate({
      coordType,
      formatErrorMsg: t(`addIncidentReport.inputs.${coordType}.formatError`),
      rangeErrorMsg: t(`addIncidentReport.inputs.${coordType}.rangeError`),
      minutesErrorMsg: t(`addIncidentReport.inputs.${coordType}.minutesError`),
      secondsErrorMsg: t(`addIncidentReport.inputs.${coordType}.secondsError`),
      directionErrorMsg: t(
        `addIncidentReport.inputs.${coordType}.directionError`,
      ),
    })
    .when(whenPropKey, {
      is: (propVal?: string) => Boolean(propVal),
      then: Yup.string()
        .nullable()
        .required(t(`addIncidentReport.inputs.${coordType}.required`)),
      otherwise: Yup.string().nullable(),
    });
};

const getTypeSpecificValidationSchema = (
  t: TFunction,
  geoEventType: ReportableGeoEvent['geoEventType'],
): Record<string, Yup.AnySchema> => {
  const schemaByType: Record<
    ReportableGeoEvent['geoEventType'],
    () => Record<string, Yup.AnySchema>
  > = {
    wildfire: () => ({
      ...getAcreageAndContainmentValidationSchema(t),
      isFps: Yup.boolean(),
      manualDeactivationStarted: Yup.boolean().nullable(),
    }),
    flooding: () => ({
      manualDeactivationStarted: Yup.boolean().nullable(),
    }),
    hazard: () => ({}),
  };

  return schemaByType[geoEventType]();
};

export const getFormValidationSchema = (
  t: TFunction,
  geoEvent: ReportableGeoEvent,
  regionEvacZones: EvacZone[],
): Yup.SchemaOf<object> => {
  const regionHasEvacZones = regionEvacZones.length > 0;

  return Yup.object().shape(
    {
      media: Yup.mixed<File | Blob | AlertCamera | null>(),
      fileType: Yup.string().nullable(),
      embedUrl: Yup.string()
        .url(t('addIncidentReport.inputs.embedUrl.invalidError'))
        .test(
          'is-supported-embed-url',
          t('addIncidentReport.inputs.embedUrl.unsupportedError'),
          (value) => (value ? isSupportedEmbedUrl(value) : true),
        ),
      geoEventId: Yup.number()
        .nullable()
        .required(t('addIncidentReport.inputs.incident.required')),
      messageHtml: getReportMessageValidationSchema(t),
      messageText: Yup.string().nullable(),
      notificationType: Yup.string().required(
        t('addIncidentReport.inputs.notificationType.required'),
      ),
      criticalAlertRegionIds: Yup.array()
        .of(Yup.number())
        .when('notificationType', {
          is: NotificationType.Critical,
          then:
            geoEvent.regions.length > 1
              ? Yup.array().min(
                  1,
                  t('editGeoEvent.inputs.criticalAlertCounties.required'),
                )
              : Yup.array(),
          otherwise: Yup.array(),
        }),
      lat: getCoordinateValidationSchema({
        t,
        coordType: 'latitude',
        whenPropKey: 'lng',
      }),
      lng: getCoordinateValidationSchema({
        t,
        coordType: 'longitude',
        whenPropKey: 'lat',
      }),
      az: Yup.number()
        .typeError(t('addIncidentReport.inputs.azimuth.typeError'))
        .min(0, t('addIncidentReport.inputs.azimuth.minError'))
        .max(360, t('addIncidentReport.inputs.azimuth.maxError')),
      status: Yup.string()
        .required(t('addIncidentReport.inputs.status.required'))
        .test(
          'no-deactivation-with-evac-zones',
          t('addIncidentReport.inputs.status.deactivationBlockedByEvacZones'),
          function (value) {
            return validateDeactivationForEvacZones({
              isDeactivating: value === 'inactive',
              evacZoneStatuses: this.parent.evacZoneStatuses ?? [],
            });
          },
        ),
      asset: Yup.string().nullable(),
      activeEvacuations: Yup.boolean(),
      ...getEvacuationFieldsValidationSchema(t, regionHasEvacZones),
      ...getEvacZoneStatusesValidationSchema(
        t,
        regionEvacZones,
        geoEvent?.evacZoneStatuses || [],
      ),
      confirmSelectedZones: Yup.boolean().when('confirmSelectedZones', {
        is: (exist: undefined | boolean) => {
          return typeof exist === 'boolean';
        },
        then: Yup.boolean().oneOf([true]),
        otherwise: Yup.boolean(),
      }),
      reporterOnlyNotes: Yup.string().nullable(),
      ...reporterNotesValidationSchema,
      customOrders: Yup.boolean(),
      customWarnings: Yup.boolean(),
      customAdvisories: Yup.boolean(),
      customShelterInPlace: Yup.boolean(),
      temporarilyDisplayEvacZones: Yup.boolean(),
      ...getTypeSpecificValidationSchema(t, geoEvent.geoEventType),
      manualDeactivationStarted: Yup.boolean()
        .nullable()
        .test(
          'no-deactivation-with-evac-zones',
          t('createEditWildfire.inputs.status.deactivationBlockedByEvacZones'),
          function (value) {
            return validateDeactivationForEvacZones({
              isDeactivating: value === true,
              evacZoneStatuses: this.parent.evacZoneStatuses ?? [],
            });
          },
        ),
    },
    [
      ['lat', 'lng'],
      ['confirmSelectedZones', 'confirmSelectedZones'],
    ],
  );
};

export const getDefaultNotificationType = (
  geoEvent: ReportableGeoEvent,
): NotificationType => {
  if (geoEvent.evacZoneStatuses.length) {
    return NotificationType.Normal;
  }

  if (isWildfireGeoEvent(geoEvent)) {
    const { containment, isFps, manualDeactivationStarted } = geoEvent.data;
    if (
      isWildfireUnderControl({
        containment,
        isFps,
        manualDeactivationStarted,
      })
    ) {
      return NotificationType.Silent;
    }
  }

  return NotificationType.Normal;
};

const getTypeSpecificInitialFormValues = (
  geoEvent: ReportableGeoEvent,
  queryParams: ReportFormQueryParams,
): Partial<ReportFormValues> => {
  const valuesByType: Record<
    ReportableGeoEvent['geoEventType'],
    () => Partial<ReportFormValues>
  > = {
    wildfire: () => {
      const wildfireGeoEvent = geoEvent as Wildfire;
      const parsedAcreage = parseFloat(queryParams.acreage ?? '');
      const queryAcreage = Number.isFinite(parsedAcreage)
        ? parsedAcreage
        : null;
      return {
        sizeUnit: 'acres',
        feetLength: null,
        feetWidth: null,
        acreage: queryAcreage ?? wildfireGeoEvent.data.acreage ?? null,
        containment: wildfireGeoEvent.data.containment ?? null,
        isFps: Boolean(wildfireGeoEvent.data.isFps),
        manualDeactivationStarted:
          typeof wildfireGeoEvent.data.manualDeactivationStarted === 'boolean'
            ? wildfireGeoEvent.data.manualDeactivationStarted
            : null,
      };
    },
    flooding: () => {
      const floodingGeoEvent = geoEvent as Flooding;
      return {
        manualDeactivationStarted:
          typeof floodingGeoEvent.data.manualDeactivationStarted === 'boolean'
            ? floodingGeoEvent.data.manualDeactivationStarted
            : null,
      };
    },
    hazard: () => {
      return {};
    },
  };

  return valuesByType[geoEvent.geoEventType]();
};

export const getInitialFormValues = (
  geoEvent: ReportableGeoEvent,
  queryParams: ReportFormQueryParams,
): ReportFormValues => {
  const evacData =
    isWildfireGeoEvent(geoEvent) || isFloodingGeoEvent(geoEvent)
      ? geoEvent.data
      : null;

  return {
    media: null,
    fileType: '',
    embedUrl: '',
    geoEventId: geoEvent.id,
    messageHtml: queryParams.message ?? '',
    messageText: removeHTMLTags(queryParams.message ?? ''),
    notificationType:
      queryParams.notificationType ?? getDefaultNotificationType(geoEvent),
    criticalAlertRegionIds: [],
    lat: '',
    lng: '',
    az: 0,
    asset: null,
    status: (geoEvent.isActive ? 'active' : 'inactive') as GeoEventFormStatus,
    // evacuation fields for wildfires and floodings
    evacZoneStatuses: geoEvent.evacZoneStatuses,
    activeEvacuations: getInitialEvacZonesVisibleState(
      isWildfireGeoEvent(geoEvent) || isFloodingGeoEvent(geoEvent)
        ? geoEvent
        : undefined,
    ),
    temporarilyDisplayEvacZones: Boolean(evacData?.temporarilyDisplayEvacZones),
    evacuationOrders: evacData?.evacuationOrders || '',
    evacuationWarnings: evacData?.evacuationWarnings || '',
    evacuationNotes: evacData?.evacuationNotes || '',
    evacuationAdvisories: evacData?.evacuationAdvisories || '',
    evacuationShelterInPlace: evacData?.evacuationShelterInPlace || '',
    customOrders: Boolean(evacData?.hasCustomEvacuationOrders),
    customWarnings: Boolean(evacData?.hasCustomEvacuationWarnings),
    customAdvisories: Boolean(evacData?.hasCustomEvacuationAdvisories),
    customShelterInPlace: Boolean(evacData?.hasCustomEvacuationShelterInPlace),
    reporterOnlyNotes: evacData?.reporterOnlyNotes || '',
    ...reporterNotesDefaultFormValues,
    ...getTypeSpecificInitialFormValues(geoEvent, queryParams),
  } as ReportFormValues;
};

export const getReviewSubmissionInitialFormValues = (
  geoEvent: ReportableGeoEvent,
  submission: CreateReportProvisionalSubmissionResponse,
): ReportFormValues => {
  const { submittedData } = submission;
  const { geoEventUpdate } = submittedData;

  // Provisional submissions cannot edit evac zones, so evacZoneStatuses is
  // never expected here. Drop it to sidestep the patch/read shape mismatch
  // (EvacZoneStatusItem[] vs FormEvacZoneStatus[]), and report to Sentry if
  // one ever slips through so we can revisit this assumption.
  const { evacZoneStatuses: unexpectedEvacZoneStatuses, ...restUpdate } =
    geoEventUpdate ?? {};

  if (unexpectedEvacZoneStatuses != null) {
    Sentry.captureMessage(
      'provisional-submission-unexpected-evac-zone-statuses',
      {
        level: 'error',
        extra: {
          submissionId: submission.id,
          geoEventId: geoEvent.id,
        },
      },
    );
  }

  const mergedGeoEvent = {
    ...geoEvent,
    ...restUpdate,
    data: {
      ...geoEvent.data,
      ...(geoEventUpdate?.data ?? {}),
    },
  } as ReportableGeoEvent;

  const base = getInitialFormValues(mergedGeoEvent, {
    message: null,
    notificationType: submittedData.notificationType,
    acreage: null,
  });

  // Need the "in" and cast here since Hazards have no reporter notes
  const pendingReporterNotes =
    geoEventUpdate && 'reporterNotes' in geoEventUpdate
      ? (geoEventUpdate.reporterNotes as ReporterNotesChanges | undefined)
      : undefined;

  const capturedMedia = submission.media?.[0];

  let mediaFields;
  if (capturedMedia) {
    mediaFields = {
      asset: 'image' as const,
      mediaUrl: capturedMedia.url,
    };
  } else if (submittedData.embedUrl) {
    mediaFields = { asset: 'socialEmbed' as const };
  } else {
    mediaFields = {};
  }

  return {
    ...base,
    messageHtml: submittedData.message ?? '',
    messageText: removeHTMLTags(submittedData.message ?? ''),
    embedUrl: submittedData.embedUrl ?? '',
    notificationType: submittedData.notificationType,
    criticalAlertRegionIds: submittedData.criticalAlertRegionIds ?? [],
    ...reporterNotesChangesToFormValues(pendingReporterNotes),
    ...mediaFields,
  };
};

export const invalidateReportsQueryCache = async (
  queryClient: QueryClient,
  geoEventId: number,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['reports', geoEventId],
    }),
    // using removeQueries for infiniteReports to fully clear old data, so no stale data is used for inflight pagination possibly overriding new data
    // see: https://tanstack.com/query/v4/docs/framework/react/reference/useInfiniteQuery#:~:text=resulting%20in%20outdated%20data
    // @linear https://linear.app/watchduty/issue/ENG-3857
    queryClient.removeQueries({
      queryKey: ['infiniteReports', geoEventId],
    }),
  ]);
};

export const postReport = async (
  data: PostReportData,
  skipRedundancyCheck?: boolean,
): Promise<void> => {
  await API.post('/reports/', {
    ...data,
    ...(skipRedundancyCheck && { skipRedundancyCheck }),
  });
};
